// src/admin/ReferralAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  addDoc,
  query,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

export default function ReferralAdmin() {
  const navigate = useNavigate();

  // tabs + filters
  const [tab, setTab] = useState('logs'); // 'logs' | 'create'
  const [filter, setFilter] = useState('');

  // current admin
  const [adminId, setAdminId] = useState(null);

  // referrals
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // name cache (uid -> displayName)
  const nameCacheRef = useRef(new Map());

  // create form
  const [referrerUid, setReferrerUid] = useState('');
  const [referredUid, setReferredUid] = useState('');
  const [campaign, setCampaign] = useState('');
  const [note, setNote] = useState('');

  // detect admin
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setAdminId(u?.uid || null));
    return () => unsub();
  }, []);

  // live referrals feed
  useEffect(() => {
    const q = query(collection(db, 'referrals'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
        setLoading(false);

        // prewarm name cache for any unseen UIDs
        const uids = new Set();
        list.forEach((r) => {
          if (r.referrerUid) uids.add(r.referrerUid);
          if (r.referredUid) uids.add(r.referredUid);
          if (r.rewardedBy) uids.add(r.rewardedBy);
          if (r.createdBy) uids.add(r.createdBy);
        });
        await warmUserNames([...uids]);
      },
      (err) => {
        console.error('❌ referrals snapshot error:', err);
        setLoading(false);
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch and cache names
  const warmUserNames = async (uids) => {
    const cache = nameCacheRef.current;
    const fetches = [];
    for (const uid of uids) {
      if (!uid) continue;
      if (!cache.has(uid)) {
        cache.set(uid, null); // placeholder to avoid dup fetch
        fetches.push(
          getDoc(doc(db, 'users', uid))
            .then((snap) => {
              const n = snap.exists() ? snap.data().name || 'Unknown' : 'Unknown';
              cache.set(uid, n);
            })
            .catch(() => cache.set(uid, 'Unknown'))
        );
      }
    }
    if (fetches.length) await Promise.all(fetches);
    // trigger re-render
    setRows((r) => [...r]);
  };

  // helpers
  const nameFor = (uid) => {
    if (!uid) return '—';
    const n = nameCacheRef.current.get(uid);
    return n ?? '…';
  };

  const displayUser = (uid) => (uid ? `${nameFor(uid)} (${uid})` : '—');

  // filtered view
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        [r.referrerUid, r.referredUid, r.status, r.campaign, r.note, r.createdBy, r.rewardedBy]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)) ||
        nameFor(r.referrerUid).toLowerCase().includes(q) ||
        nameFor(r.referredUid).toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter]);

  // actions
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!referrerUid || !referredUid) return alert('❌ Referrer & Referred UIDs required');
    if (referrerUid === referredUid) return alert('❌ Referrer and Referred cannot be the same UID');

    try {
      await addDoc(collection(db, 'referrals'), {
        referrerUid: referrerUid.trim(),
        referredUid: referredUid.trim(),
        campaign: campaign.trim() || null,
        note: note.trim() || null,
        status: 'pending', // pending | rewarded | fraud | rejected
        timestamp: serverTimestamp(),
        createdBy: adminId || 'system',
      });

      alert('✅ Referral record added.');
      setReferrerUid('');
      setReferredUid('');
      setCampaign('');
      setNote('');
    } catch (err) {
      console.error('❌ failed to create referral:', err);
      alert('Failed to create referral record.');
    }
  };

  const markRewarded = async (id) => {
    if (!adminId) return alert('Missing admin auth');
    try {
      await updateDoc(doc(db, 'referrals', id), {
        status: 'rewarded',
        rewardedAt: Timestamp.now(),
        rewardedBy: adminId,
      });
    } catch (err) {
      console.error('❌ markRewarded error:', err);
      alert('Failed to mark as rewarded.');
    }
  };

  const markFlag = async (id, status = 'fraud') => {
    try {
      await updateDoc(doc(db, 'referrals', id), {
        status,
        flaggedAt: Timestamp.now(),
        flaggedBy: adminId || 'system',
      });
    } catch (err) {
      console.error('❌ flag error:', err);
      alert('Failed to update status.');
    }
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back
      </button>
      <h2 style={styles.header}>👥 Referral Admin</h2>

      <div style={styles.tabButtons}>
        <button onClick={() => setTab('logs')} style={tab === 'logs' ? styles.activeTab : styles.tab}>
          View Referrals
        </button>
        <button onClick={() => setTab('create')} style={tab === 'create' ? styles.activeTab : styles.tab}>
          Create Referral
        </button>
      </div>

      {tab === 'create' && (
        <form onSubmit={handleCreate} style={styles.form}>
          <h3>➕ New Referral</h3>
          <input
            placeholder="Referrer UID"
            value={referrerUid}
            onChange={(e) => setReferrerUid(e.target.value)}
            style={styles.input}
          />
          <input
            placeholder="Referred UID"
            value={referredUid}
            onChange={(e) => setReferredUid(e.target.value)}
            style={styles.input}
          />
          <input
            placeholder="Campaign (optional)"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            style={styles.input}
          />
          <input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={styles.input} />
          <button type="submit" style={styles.submitButton}>
            Create
          </button>
        </form>
      )}

      {tab === 'logs' && (
        <>
          <input
            type="text"
            placeholder="Search by name, UID, status, campaign…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.search}
          />
          {loading ? (
            <p>Loading referrals…</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Timestamp</th>
                  <th style={styles.th}>Referrer</th>
                  <th style={styles.th}>Referred</th>
                  <th style={styles.th}>Campaign</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Created By</th>
                  <th style={styles.th}>Reward</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td style={styles.td}>{r.timestamp?.toDate?.().toLocaleString?.() || '—'}</td>
                    <td style={styles.td}>{displayUser(r.referrerUid)}</td>
                    <td style={styles.td}>{displayUser(r.referredUid)}</td>
                    <td style={styles.td}>{r.campaign || '—'}</td>
                    <td style={styles.td}>
                      <span style={badgeColor(r.status)}>{r.status || 'pending'}</span>
                    </td>
                    <td style={styles.td}>{r.createdBy ? displayUser(r.createdBy) : 'system'}</td>
                    <td style={styles.td}>
                      {r.status === 'rewarded' ? (
                        <span>
                          ✅ Rewarded {r.rewardedAt?.toDate?.().toLocaleDateString?.() || ''}
                          {r.rewardedBy ? ` by ${nameFor(r.rewardedBy)}` : ''}
                        </span>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => markRewarded(r.id)} style={styles.smallBtn}>
                            Mark Rewarded
                          </button>
                          <button onClick={() => markFlag(r.id, 'fraud')} style={styles.smallWarnBtn}>
                            Flag Fraud
                          </button>
                          <button onClick={() => markFlag(r.id, 'rejected')} style={styles.smallGrayBtn}>
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

/* ---------- styles ---------- */
const styles = {
  container: {
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    fontFamily: 'system-ui, sans-serif',
    minHeight: '100vh',
  },
  backButton: {
    marginBottom: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#eee',
    cursor: 'pointer',
  },
  header: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#084298',
  },
  tabButtons: { marginBottom: '1rem' },
  tab: {
    marginRight: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#f0f0f0',
    cursor: 'pointer',
  },
  activeTab: {
    fontWeight: 'bold',
    backgroundColor: '#d1e7dd',
    marginRight: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #999',
    cursor: 'pointer',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '420px',
    marginBottom: '2rem',
  },
  input: {
    marginBottom: '0.75rem',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
  },
  submitButton: {
    padding: '0.6rem',
    borderRadius: '6px',
    backgroundColor: '#0f5132',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
  },
  search: {
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    width: '100%',
    maxWidth: '320px',
    marginBottom: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
  },
  th: {
    borderBottom: '2px solid #ccc',
    padding: '0.75rem',
    backgroundColor: '#e6f2ff',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  td: {
    borderBottom: '1px solid #ddd',
    padding: '0.75rem',
    verticalAlign: 'top',
  },
  smallBtn: {
    padding: '0.35rem 0.5rem',
    borderRadius: '6px',
    backgroundColor: '#198754',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
  },
  smallWarnBtn: {
    padding: '0.35rem 0.5rem',
    borderRadius: '6px',
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
  },
  smallGrayBtn: {
    padding: '0.35rem 0.5rem',
    borderRadius: '6px',
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
  },
};

function badgeColor(status = 'pending') {
  const base = {
    padding: '0.25rem 0.5rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'capitalize',
  };
  switch (status) {
    case 'rewarded':
      return { ...base, backgroundColor: '#d1e7dd', color: '#0f5132' };
    case 'fraud':
      return { ...base, backgroundColor: '#f8d7da', color: '#842029' };
    case 'rejected':
      return { ...base, backgroundColor: '#e2e3e5', color: '#41464b' };
    default:
      return { ...base, backgroundColor: '#fff3cd', color: '#664d03' }; // pending
  }
}
